import type {
  BookReadingDto,
  ReadingOverviewDto,
  ReadingSessionCreateInput,
  ReadingSessionDto,
  ReadingSessionPatchInput,
} from "@shelflist/shared";
import { queryOptions } from "@tanstack/react-query";
import { api, call } from "./api";

export const readingKeys = {
  all: ["reading"] as const,
  book: (bookId: string) => ["reading", "book", bookId] as const,
  overview: ["reading", "overview"] as const,
};

export const bookReadingQuery = (bookId: string) =>
  queryOptions({
    queryKey: readingKeys.book(bookId),
    queryFn: () =>
      call(api.books[":id"].reading.$get({ param: { id: bookId } })) as Promise<BookReadingDto>,
  });

export const readingOverviewQuery = queryOptions({
  queryKey: readingKeys.overview,
  queryFn: () => call(api.me.reading.$get()) as Promise<ReadingOverviewDto>,
});

/** The user's local calendar date (`YYYY-MM-DD`), which is what the server expects for "today". */
export function localToday(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export const readingApi = {
  setWantToRead: (bookId: string, wanted: boolean) =>
    wanted
      ? call(api.books[":id"]["want-to-read"].$put({ param: { id: bookId } }))
      : call(api.books[":id"]["want-to-read"].$delete({ param: { id: bookId } })),
  createSession: (bookId: string, json: ReadingSessionCreateInput) =>
    call(
      api.books[":id"]["reading-sessions"].$post({ param: { id: bookId }, json }),
    ) as Promise<ReadingSessionDto>,
  patchSession: (id: string, json: ReadingSessionPatchInput) =>
    call(
      api["reading-sessions"][":id"].$patch({ param: { id }, json }),
    ) as Promise<ReadingSessionDto>,
  endSession: (id: string, kind: "finish" | "abandon", date: string) =>
    call(
      api["reading-sessions"][":id"][kind].$post({ param: { id }, json: { date } }),
    ) as Promise<ReadingSessionDto>,
  deleteSession: (id: string) => call(api["reading-sessions"][":id"].$delete({ param: { id } })),
};
